import { useState, useEffect, useRef, useMemo } from 'react'
import { Hospital } from '@/lib/api/hospitals/hospital-api'
import { MdSearch, MdLocationCity, MdCheck } from 'react-icons/md'
import { FaHospital } from 'react-icons/fa6'

interface HospitalSelectorProps {
    hospitals: Hospital[]
    selectedId: number | null
    onChange: (id: number) => void
    loading?: boolean
    maxVisible?: number
}

export function HospitalSelector({
    hospitals,
    selectedId,
    onChange,
    loading = false,
    maxVisible = 3,
}: HospitalSelectorProps) {
    const [searchQuery, setSearchQuery] = useState('')
    const [showDropdown, setShowDropdown] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)

    const selectedHospital = hospitals.find(h => h.id === selectedId)

    const shuffled = useMemo(() => {
        const arr = [...hospitals]
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            ;[arr[i], arr[j]] = [arr[j], arr[i]]
        }
        return arr
    }, [hospitals])

    const visibleHospitals = selectedHospital
        ? [
            selectedHospital,
            ...shuffled
                .filter(h => h.id !== selectedHospital.id)
                .slice(0, maxVisible - 1),
        ]
        : shuffled.slice(0, maxVisible)

    const filteredHospitals = searchQuery.trim()
        ? hospitals.filter(h =>
            h.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (h.tradingName?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false) ||
            (h.city?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false)
        )
        : hospitals

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setShowDropdown(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleSelect = (id: number) => {
        onChange(id)
        setShowDropdown(false)
        setSearchQuery('')
    }

    const getInitials = (name: string) =>
        name
            .split(' ')
            .map(n => n[0])
            .slice(0, 2)
            .join('')
            .toUpperCase()

    const getAvatarColor = (id: number) => {
        const colors = [
            'bg-blue-500',
            'bg-green-500',
            'bg-purple-500',
            'bg-orange-500',
            'bg-pink-500',
            'bg-cyan-500',
            'bg-indigo-500',
            'bg-teal-500',
        ]
        return colors[id % colors.length]
    }

    if (loading) {
        return (
            <div className="flex items-center gap-3">
                <div className="skeleton h-12 w-12 rounded-full"></div>
                <div className="skeleton h-12 w-12 rounded-full"></div>
                <div className="skeleton h-10 w-48 rounded-lg"></div>
            </div>
        )
    }

    if (hospitals.length === 0) {
        return (
            <div className="flex items-center gap-2 text-sm text-base-content/60">
                <FaHospital className="text-lg" />
                <span>Nenhum hospital cadastrado</span>
            </div>
        )
    }

    // Quando ha apenas 1 hospital, mostrar como info nao-interativa
    if (hospitals.length === 1) {
        const h = hospitals[0]
        return (
            <div className="flex items-center gap-2 px-3 py-2 bg-base-200 rounded-lg">
                <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${getAvatarColor(h.id)}`}
                >
                    {getInitials(h.name)}
                </div>
                <div className="flex flex-col">
                    <span className="text-sm font-medium">{h.tradingName || h.name}</span>
                    {h.city && (
                        <span className="text-xs text-base-content/60">{h.city}</span>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div ref={containerRef} className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
                {visibleHospitals.map(hospital => {
                    const isSelected = hospital.id === selectedId
                    return (
                        <div
                            key={hospital.id}
                            className="tooltip tooltip-bottom"
                            data-tip={hospital.name}
                        >
                            <button
                                onClick={() => handleSelect(hospital.id)}
                                className={`
                                    btn
                                    ${isSelected
                                        ? 'bg-secondary text-primary-content shadow-lg '
                                        : 'bg-base-200 hover:bg-base-300 text-base-content'
                                    }
                                `}
                            >
                                <div
                                    className={`
                                        w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold
                                        ${isSelected ? 'bg-primary-focus' : getAvatarColor(hospital.id)}
                                    `}
                                >
                                    {getInitials(hospital.name)}
                                </div>
                                <span className="text-sm font-medium max-w-[140px] truncate">
                                    {hospital.tradingName || hospital.name.split(' ')[0]}
                                </span>
                                {isSelected && (
                                    <MdCheck className="text-lg flex-shrink-0" />
                                )}
                            </button>
                        </div>
                    )
                })}
            </div>

            <div className="relative">
                <label className="input input-bordered ">
                    <MdSearch className="text-base-content/50 text-lg" />
                    <input
                        type="text"
                        placeholder="Buscar hospital..."
                        className=""
                        value={searchQuery}
                        onChange={e => {
                            setSearchQuery(e.target.value)
                            setShowDropdown(true)
                        }}
                        onFocus={() => setShowDropdown(true)}
                    />
                </label>

                {showDropdown && (
                    <div className="absolute z-50 top-full left-0 mt-2 w-80 bg-base-100 border border-base-300 rounded-xl shadow-2xl max-h-80 overflow-y-auto">
                        {filteredHospitals.length === 0 ? (
                            <div className="p-4 text-center text-base-content/60">
                                <FaHospital className="text-3xl mx-auto mb-2 opacity-50" />
                                <p className="text-sm">
                                    Nenhum hospital encontrado
                                </p>
                            </div>
                        ) : (
                            <div className="py-2">
                                {filteredHospitals.map(hospital => {
                                    const isSelected = hospital.id === selectedId
                                    return (
                                        <button
                                            key={hospital.id}
                                            onClick={() => handleSelect(hospital.id)}
                                            className={`
                                                w-full flex items-center gap-3 px-4 py-3 text-left
                                                transition-colors duration-150
                                                ${isSelected
                                                    ? 'bg-secondary/10'
                                                    : 'hover:bg-base-200'
                                                }
                                            `}
                                        >
                                            <div
                                                className={`
                                                    w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold
                                                    ${getAvatarColor(hospital.id)}
                                                `}
                                            >
                                                {getInitials(hospital.name)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p
                                                    className={`font-medium truncate ${isSelected ? 'text-secondary' : ''}`}
                                                >
                                                    {hospital.tradingName || hospital.name}
                                                </p>
                                                {hospital.city && (
                                                    <p className="text-xs text-base-content/60 truncate">
                                                        {hospital.city}{hospital.state ? ` - ${hospital.state}` : ''}
                                                    </p>
                                                )}
                                            </div>
                                            {isSelected && (
                                                <MdCheck className="text-secondary text-xl flex-shrink-0" />
                                            )}
                                        </button>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {selectedHospital && (
                <div className="md:hidden flex items-center gap-2 text-sm text-base-content/70">
                    <MdLocationCity />
                    <span className="truncate max-w-[150px]">
                        {selectedHospital.tradingName || selectedHospital.name}
                    </span>
                </div>
            )}
        </div>
    )
}
